import axios from "axios";

const base = process.env.REACT_APP_BACK_URL || `${window.location.protocol}//${window.location.hostname}:5000`;

type Headers = { headers?: Record<string, string> };

const auth = (token?: string): Headers =>
  token
    ? {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    : {};

export type Campania = {
  campania_id: number;
  nombre: string;
  descripcion?: string;
  logo_url?: string;
  imagen_url?: string;
  fecha_inicio: string;
  fecha_fin: string;
  estado: 'borrador' | 'activa' | 'finalizada' | 'cancelada';
  descuento?: string;
  terminos?: string;
  creado_en: string;
  modificado_en?: string;
};

export type SocioCampania = {
  socio_campania_id: number;
  socio_id: number;
  campania_id: number;
  estado: 'pendiente' | 'aceptada' | 'rechazada';
  aceptado_en?: string;
  notas?: string;
  creado_en: string;
  campania?: Campania;
};

export type SocioEnCampania = {
  socio_campania_id: number;
  socio_id: number;
  socio_nombre: string | null;
  socio_mail: string | null;
  socio_celular: string | null;
  estado: string;
  aceptado_en?: string;
  notas?: string;
  creado_en: string;
};

export const listarCampanias = async (estado?: string, token?: string) => {
  const params: any = {};
  if (estado) params.estado = estado;
  const { data } = await axios.get<Campania[]>(`${base}/campanias`, {
    ...(auth(token) || {}),
    params,
  });
  return data;
};

export const obtenerCampaniaPorId = async (campaniaId: number, token?: string) => {
  const { data } = await axios.get<Campania>(`${base}/campanias/${campaniaId}`, auth(token));
  return data;
};

export const crearCampania = async (
  payload: {
    nombre: string;
    descripcion?: string;
    logo_url?: string;
    imagen_url?: string;
    fecha_inicio: string;
    fecha_fin: string;
    estado?: string;
    descuento?: string;
    terminos?: string;
  },
  token?: string
) => {
  const { data } = await axios.post(`${base}/campanias`, payload, auth(token));
  return data;
};

export const actualizarCampania = async (
  campaniaId: number,
  payload: Partial<{
    nombre: string;
    descripcion: string;
    logo_url: string;
    imagen_url: string;
    fecha_inicio: string;
    fecha_fin: string;
    estado: string;
    descuento: string;
    terminos: string;
  }>,
  token?: string
) => {
  const { data } = await axios.put(`${base}/campanias/${campaniaId}`, payload, auth(token));
  return data;
};

export const eliminarCampania = async (campaniaId: number, token?: string) => {
  const { data } = await axios.delete(`${base}/campanias/${campaniaId}`, auth(token));
  return data;
};

export const asociarSocioACampania = async (
  payload: { socioId: number; campaniaId: number; notas?: string },
  token?: string
) => {
  const { data } = await axios.post(`${base}/campanias/asociar`, payload, auth(token));
  return data;
};

export const asociarSociosBulk = async (
  payload: { socioIds: number[]; campaniaId: number; notas?: string },
  token?: string
) => {
  const { data } = await axios.post(`${base}/campanias/asociar-bulk`, payload, auth(token));
  return data;
};

export const aceptarCampania = async (socioCampaniaId: number, token?: string) => {
  const { data } = await axios.post(`${base}/campanias/aceptar/${socioCampaniaId}`, {}, auth(token));
  return data;
};

export const rechazarCampania = async (socioCampaniaId: number, token?: string) => {
  const { data } = await axios.post(`${base}/campanias/rechazar/${socioCampaniaId}`, {}, auth(token));
  return data;
};

export const listarCampaniasPorSocio = async (socioId: number, token?: string) => {
  const { data } = await axios.get<SocioCampania[]>(`${base}/campanias/socio/${socioId}`, auth(token));
  return data;
};

export const listarSociosPorCampania = async (campaniaId: number, token?: string) => {
  const { data } = await axios.get<SocioEnCampania[]>(`${base}/campanias/${campaniaId}/socios`, auth(token));
  return data;
};

export const eliminarAsociacionCampania = async (socioCampaniaId: number, token?: string) => {
  const { data } = await axios.delete(`${base}/campanias/asociacion/${socioCampaniaId}`, auth(token));
  return data;
};

export const subirLogoCampania = async (
  campaniaId: number,
  file: File,
  getAccessTokenSilently: () => Promise<string>
): Promise<string> => {
  const token = await getAccessTokenSilently();
  const formData = new FormData();
  formData.append("file", file);

  const response = await axios.post<{ logoUrl: string }>(
    `${base}/files/campanias/${campaniaId}/logo`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
    }
  );

  return response.data.logoUrl;
};

export const subirImagenCampania = async (
  campaniaId: number,
  file: File,
  getAccessTokenSilently: () => Promise<string>
): Promise<string> => {
  const token = await getAccessTokenSilently();
  const formData = new FormData();
  formData.append("file", file);

  const response = await axios.post<{ imagenUrl: string }>(
    `${base}/files/campanias/${campaniaId}/imagen`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
    }
  );

  return response.data.imagenUrl;
};
